import type { FinanceCashRecord } from "./finance-workbook-query";
import type { FinanceRecordFilters } from "./finance-records";
import { financeRecordTotals } from "./finance-records";

export type FinanceExportContext = {
  title: string; company: string; date: string; notes: string[];
  observedAt?: string; scopeTotal?: { amount: bigint | null; currency: string };
};

/** Large amounts remain decimal text; an Excel number has only 15 significant digits. */
export function financeExcelAmount(cents: bigint): number | string {
  const absolute = cents < 0n ? -cents : cents;
  if (absolute <= 999_999_999_999_99n) return Number(cents) / 100;
  return `${cents < 0n ? "-" : ""}${absolute / 100n}.${String(absolute % 100n).padStart(2, "0")}`;
}

export async function buildFinanceRecordsWorkbook(records: FinanceCashRecord[], filters: FinanceRecordFilters, context: FinanceExportContext) {
  const { default: ExcelJS } = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "ANL Finans";
  workbook.created = new Date();
  const info = workbook.addWorksheet("Rapor bilgisi");
  info.columns = [{ width: 27 }, { width: 100 }];
  info.addRows([["ANL · Nakit akışı", context.title], ["Şirket", context.company], ["Rapor tarihi", context.date],
    ["Kaynak okuma zamanı", context.observedAt ?? "Kaynak bazında değişebilir"], ["Dışa aktarılan kayıt", records.length],
    ["Arama", filters.search || "Tümü"], ["Cari / hesap", filters.account || "Tümü"],
    ["Vade başlangıcı", filters.from || "Sınır yok"], ["Vade bitişi", filters.to || "Sınır yok"],
    ["Döviz filtresi", filters.currency === "all" ? "Orijinal dövizler ayrı" : filters.currency],
    ["Yön", ({ all: "Tümü", incoming: "Giriş", outgoing: "Çıkış", none: "Bakiye" })[filters.direction]],
    ["Vade durumu", ({ all: "Tümü", overdue: "Gecikmiş", upcoming: "Önümüzdeki 7 gün", undated: "Tarihsiz" })[filters.due]],
    ["Hesaba dahil", ({ all: "Tümü", included: "Dahil", excluded: "Hariç" })[filters.inclusion]],
    ["Sıralama", filters.sort]]);
  if (context.scopeTotal) info.addRow([`Tıklanan kapsam (${context.scopeTotal.currency})`,
    context.scopeTotal.amount === null ? "Hesaplanamadı" : financeExcelAmount(context.scopeTotal.amount)]);
  for (const note of context.notes) info.addRow(["Kapsam / açıklama", note]);
  info.addRow(["Tutarlar", "Her kayıt orijinal dövizindedir. Farklı dövizler birlikte toplanmaz. Filtrelenmiş alt toplam, tıklanan kapsamdan daha dar olabilir."]);
  info.addRow(["Hassasiyet", "Excel hassasiyetini aşan tutarlar ondalık metin olarak korunur. Kaynak açıklamaları metindir; formül çalıştırılmaz."]);
  const sheet = workbook.addWorksheet("Kayıtlar", { views: [{ state: "frozen", ySplit: 1 }] });
  sheet.columns = [
    { header: "Kaynak", key: "sheet", width: 23 }, { header: "Cari / hesap", key: "code", width: 24 },
    { header: "Unvan", key: "name", width: 42 }, { header: "Belge / taksit", key: "document", width: 28 },
    { header: "Tarih / vade", key: "date", width: 16 }, { header: "Döviz", key: "currency", width: 12 },
    { header: "Yön", key: "direction", width: 15 }, { header: "Tutar", key: "amount", width: 23 },
    { header: "Hesaba dahil", key: "included", width: 17 }, { header: "Kaynak durumu", key: "status", width: 48 },
    { header: "Kayıt kimliği", key: "identity", width: 40 }, { header: "Kaynak tutarı", key: "sourceAmount", width: 23 },
    { header: "Kaynakta ödenen", key: "paid", width: 23 }, { header: "Kalan", key: "remaining", width: 23 },
  ];
  for (const record of records) sheet.addRow({ sheet: record.sheetId, code: record.row.code, name: record.row.name,
    document: record.row.document, date: record.row.date ?? (record.row.direction === "none" || record.movementId === "opening" ? "Rapor bakiyesi" : "Tarihi belirsiz"), currency: record.row.currency,
    direction: record.row.direction === "outgoing" ? "Çıkış" : record.row.direction === "incoming" ? "Giriş" : "Bakiye",
    amount: financeExcelAmount(record.amount), included: record.included ? "Dahil" : "Hariç", status: record.row.status,
    identity: record.key, sourceAmount: financeExcelAmount(BigInt(record.row.amount)),
    paid: record.row.paid === null ? "Doğrulanmadı" : financeExcelAmount(BigInt(record.row.paid)),
    remaining: financeExcelAmount(BigInt(record.row.remaining)) });
  sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: Math.max(1, sheet.rowCount), column: 14 } };
  for (const column of ["amount", "sourceAmount", "paid", "remaining"]) sheet.getColumn(column).numFmt = '#,##0.00;[Red]-#,##0.00';
  const totals = workbook.addWorksheet("Döviz toplamları");
  totals.columns = [{ header: "Döviz", width: 15 }, { header: "Giriş", width: 25 }, { header: "Çıkış", width: 25 }, { header: "Net / bakiye", width: 25 }];
  for (const entry of financeRecordTotals(records)) totals.addRow([entry.currency, financeExcelAmount(entry.incoming), financeExcelAmount(entry.outgoing), financeExcelAmount(entry.total)]);
  for (const column of [2, 3, 4]) totals.getColumn(column).numFmt = '#,##0.00;[Red]-#,##0.00';
  for (const page of [info, sheet, totals]) {
    page.getRow(1).font = { name: "Aptos", bold: true, color: { argb: "FFFFFFFF" } };
    page.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF243B40" } };
    page.getRow(1).height = 28;
    page.eachRow((row, index) => { row.alignment = { vertical: "top", wrapText: true }; if (index > 1) row.font = { name: "Aptos", size: 11 }; });
    page.pageSetup = { paperSize: 9, orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0 };
  }
  return new Uint8Array(await workbook.xlsx.writeBuffer());
}

export async function downloadFinanceRecords(records: FinanceCashRecord[], filters: FinanceRecordFilters, context: FinanceExportContext) {
  const bytes = await buildFinanceRecordsWorkbook(records, filters, context);
  const blob = new Blob([new Uint8Array(bytes)], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob), link = document.createElement("a");
  link.href = url; link.download = `ANL-Finans-${context.date}.xlsx`;
  document.body.append(link); link.click(); link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
